import type { AxiosRequestConfig } from "axios";
import type { ApiRequestHelpers } from "./useHttpClient";
import type {
  FilterByQuery,
  IncludeQuery,
  OpenTrajectory,
  PaginationQuery,
  ManagedTrajectoriesResponse,
  Response,
  SortByQuery,
  Todo,
  TrajectoryReminder,
} from "~/types/api";

export function createTrajectoryApi({ getRequest, getPaginationRequest, postRequest }: ApiRequestHelpers) {
  /** Fetches a paginated list of open trajectories across the organization. */
  const openTrajectories = (
    pagination?: PaginationQuery,
    sortBy?: SortByQuery,
    includes?: IncludeQuery,
    filterBy?: FilterByQuery,
  ) => getPaginationRequest<OpenTrajectory>("/trajectories/overview", pagination, sortBy, includes, filterBy);

  /** Fetches recorded practitioner-role and care-location groups with role-keyed reminders. */
  const managedTrajectories = (config?: AxiosRequestConfig) =>
    getRequest<ManagedTrajectoriesResponse>("/trajectories/overview/managed", config ?? {});

  /** Sends a reminder todo to a physio about their open trajectories and upserts a TrajectoryReminder. */
  const remindPhysio = (data: { practitioner_role_id: string }) =>
    postRequest<Response<Todo> & { reminder: TrajectoryReminder }>("/trajectories/overview/remind", data);

  /** Counts the current physio's open trajectories without activity for at least the organization's threshold. */
  const inactiveTrajectoriesCount = () =>
    getRequest<Response<{ count: number; days_threshold: number }>>("/trajectories/overview/inactive-count");

  return { openTrajectories, managedTrajectories, remindPhysio, inactiveTrajectoriesCount };
}

export type TrajectoryApi = ReturnType<typeof createTrajectoryApi>;
